/**
 * Utility for dynamically calculating how many items can fit in a container.
 * Uses ResizeObserver for efficient resize handling with minimal listeners.
 *
 * Supports two layout modes:
 * 1. Single column (default): items stack vertically
 * 2. Grid layout: items wrap after N columns per row
 */

export interface MaxVisibleConfig {
  /** Height of each item/row in pixels */
  itemHeight: number;
  /** Gap between items in pixels */
  gap: number;
  /** Container padding (top + bottom) in pixels */
  containerPadding?: number;
  /** Height reserved for "more" button in pixels (for single column) */
  moreButtonHeight?: number;
  /** Minimum items to show regardless of space */
  minVisible?: number;
  /** Default items to show before measurement */
  defaultVisible?: number;
  /**
   * Number of columns for grid layout.
   * If set (>= 1), calculates as rows × columns with last slot reserved for "view more".
   * If not set, uses single-column calculation with moreButtonHeight.
   */
  columns?: number;
}

export interface MaxVisibleResult {
  /** Current max visible count */
  maxVisible: number;
  /** Cleanup function to disconnect observer */
  destroy: () => void;
}

/**
 * Calculate max visible items that fit in a container's available height.
 *
 * For single-column layouts: returns number of items that fit vertically.
 * For grid layouts: returns (rows × columns - 1) to reserve last slot for "view more".
 *
 * @param container - The container element to observe
 * @param config - Configuration for item dimensions
 * @param onUpdate - Callback when maxVisible changes
 * @returns Cleanup function
 *
 * @example Single column
 * ```svelte
 * setupMaxVisible(container, {
 *   itemHeight: 20,
 *   gap: 4,
 *   moreButtonHeight: 24
 * }, (max) => maxVisible = max);
 * ```
 *
 * @example Grid layout (4 columns)
 * ```svelte
 * setupMaxVisible(container, {
 *   itemHeight: 24,  // row height
 *   gap: 4,
 *   columns: 4       // 4 items per row
 * }, (max) => maxVisible = max);
 * // If 2 rows fit: maxVisible = (2 × 4) - 1 = 7
 * ```
 */
export function setupMaxVisible(
  container: HTMLElement,
  config: MaxVisibleConfig,
  onUpdate: (maxVisible: number) => void,
): () => void {
  const {
    itemHeight,
    gap,
    containerPadding = 0,
    moreButtonHeight = 0,
    // minVisible is accepted via config but not currently used in calculation
    columns,
  } = config;

  // Add per-row buffer for subpixel rendering, font metric variations,
  // and border rounding that can make items slightly taller than estimated.
  const RENDER_BUFFER_PER_ROW = 2;
  const rowHeight = itemHeight + gap + RENDER_BUFFER_PER_ROW;

  const calculate = () => {
    if (!container) return;

    let maxVisible: number;

    if (columns && columns >= 1) {
      // Grid layout: calculate rows that fit, then multiply by columns
      // Reserve last slot for "view more" chip (it occupies a grid cell)
      const availableHeight = container.clientHeight - containerPadding;
      const maxRows = Math.max(1, Math.floor(availableHeight / rowHeight));
      const totalSlots = maxRows * columns;
      // Never exceed physical capacity — minVisible must not override physics
      maxVisible = Math.max(1, totalSlots - 1);
    } else {
      // Single column (no columns param): simple vertical stacking
      // moreButtonHeight reserves space for the "view more" button below items
      const availableHeight = container.clientHeight - containerPadding - moreButtonHeight;
      const physicalFit = Math.floor(availableHeight / rowHeight);
      // Cap minVisible at what physically fits to prevent overflow
      maxVisible = Math.max(1, physicalFit);
    }

    onUpdate(maxVisible);
  };

  // Immediate synchronous calculation — critical when called from a
  // resize callback (column change) so maxVisible updates in the same
  // frame as the column count, preventing a flash of too-many items.
  calculate();

  // Also schedule a deferred calculation for first-mount scenarios where
  // the container may not have its final dimensions yet (e.g. flex layout
  // hasn't resolved). The double-rAF ensures layout has fully settled.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      calculate();
    });
  });

  // Recalculate on resize
  const resizeObserver = new ResizeObserver(() => {
    requestAnimationFrame(calculate);
  });
  resizeObserver.observe(container);

  // Return cleanup function
  return () => resizeObserver.disconnect();
}

/**
 * Svelte action for dynamic max visible calculation.
 *
 * @example
 * ```svelte
 * <div use:maxVisibleAction={{
 *   config: { itemHeight: 20, gap: 4 },
 *   onUpdate: (max) => maxVisible = max
 * }}>
 * ```
 */
export function maxVisibleAction(
  node: HTMLElement,
  params: {
    config: MaxVisibleConfig;
    onUpdate: (maxVisible: number) => void;
  },
) {
  const destroy = setupMaxVisible(node, params.config, params.onUpdate);

  return {
    destroy,
  };
}

/**
 * Configuration presets for common item types
 */
export const MAX_VISIBLE_PRESETS = {
  /** Project chips in single-column layout (measured: 19.5px) */
  projectChip: {
    itemHeight: 19.5,
    gap: 2,
    containerPadding: 2,
    moreButtonHeight: 20,
    minVisible: 1,
    defaultVisible: 5,
  },
  /** Event items in single-column layout (measured: 19.5px) */
  eventChip: {
    itemHeight: 19.5,
    gap: 2,
    containerPadding: 2,
    moreButtonHeight: 20,
    minVisible: 1,
    defaultVisible: 4,
  },
  /** Compact event dots in day columns (measured: 14px, +0.5 buffer) */
  eventDot: {
    itemHeight: 14.5,
    gap: 2,
    containerPadding: 0,
    moreButtonHeight: 16,
    minVisible: 1,
    defaultVisible: 3,
  },
  /** Project chips in 4-column grid (measured: 19.5px row height) */
  projectChipGrid: {
    itemHeight: 19.5,
    gap: 4,
    containerPadding: 2,
    columns: 4,
    minVisible: 3,
    defaultVisible: 7,
  },
  /** Event chips in 4-column grid (measured: 19.5px row height) */
  eventChipGrid: {
    itemHeight: 19.5,
    gap: 4,
    containerPadding: 2,
    columns: 4,
    minVisible: 3,
    defaultVisible: 7,
  },
} as const;
